// FalsePositiveModal - prompts the operator for a reason before flagging a
// finding as false positive, plus the optional "apply to similar" batch
// suppression. The similarCount comes from the parent via the same prefix
// matching the backend uses, so the operator sees a real number before the
// click instead of trusting the backend silently.
import { useEffect, useState } from 'react';
import { ShieldOff, X } from 'lucide-react';
import { PrimaryCTA } from '../../common/Shinobi';

export default function FalsePositiveModal({
  isOpen,
  finding,
  similarCount = 0,
  submitting = false,
  onClose,
  onSubmit,
}) {
  const [reason, setReason] = useState('');
  const [suppressSimilar, setSuppressSimilar] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setReason('');
      setSuppressSimilar(false);
    }
  }, [isOpen, finding?.id]);

  if (!isOpen || !finding) return null;

  const canSubmit = reason.trim().length > 0 && !submitting;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({ reason: reason.trim(), suppressSimilar: suppressSimilar && similarCount > 0 });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-2xl border border-cyber-300/40 bg-cyber-50 shadow-[0_30px_80px_rgba(0,0,0,0.6)] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cyber-300/40 bg-amber-100">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-100 text-amber-700 border border-amber-300">
              <ShieldOff className="w-4 h-4" />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">Mark as false positive</div>
              <div className="text-sm font-semibold text-slate-900 truncate max-w-[24rem]">
                #{finding.reference_number} - {finding.title}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1.5 rounded text-slate-500 hover:text-slate-800 hover:bg-cyber-200/40 disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1.5">
              Why is this a false positive? <span className="text-amber-700">*</span>
            </label>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="e.g. Endpoint accepts arbitrary path segments after / and returns 200; nuclei treats every path as a distinct exposure but the app intentionally normalizes."
              className="w-full bg-cyber-100 border border-cyber-300/60 rounded-lg px-3 py-2 text-sm text-slate-900 outline-none focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/20 resize-y"
            />
            <div className="text-[10px] text-slate-500 mt-1 text-right">
              {reason.length}/2000
            </div>
          </div>

          {similarCount > 0 && (
            <label className="flex items-start gap-3 p-3 rounded-lg border border-cyber-300/40 bg-cyber-100/50 cursor-pointer hover:border-amber-300 transition">
              <input
                type="checkbox"
                checked={suppressSimilar}
                onChange={(e) => setSuppressSimilar(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-amber-400"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-900">
                  Also mark <span className="text-amber-700 font-bold">{similarCount}</span> similar finding{similarCount === 1 ? '' : 's'} as FP
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Same scanner + same template + same target host (different URLs / paths). They will share this reason.
                </div>
              </div>
            </label>
          )}

          <div className="text-[11px] text-slate-500 leading-relaxed">
            False positives are excluded from the PT severity counts and grade calculation, but stay in the database for the record. Use the eye toggle on the Findings tab to view them later.
          </div>
        </div>

        <div className="px-5 py-3 border-t border-cyber-300/40 bg-cyber-100/30 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded-lg text-sm text-slate-700 hover:bg-cyber-200/40 disabled:opacity-40"
          >
            Cancel
          </button>
          <PrimaryCTA
            variant="purple"
            icon={ShieldOff}
            iconPosition="left"
            loading={submitting}
            onClick={submit}
            disabled={!canSubmit}
          >
            Mark as false positive
          </PrimaryCTA>
        </div>
      </div>
    </div>
  );
}
